import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Plus, Trash2, Save, Percent } from "lucide-react";
import { toast } from "sonner";
import {
  createSellerCommissionRule,
  updateSellerCommissionRule,
} from "@/Api/sellerCommission";
import { getUnitiesData } from "@/Api/unities";
import { MultiSelectCheckbox } from "@/components/ui/multi-select-checkbox";

// Formata número para moeda brasileira (1.000,00)
const formatCurrencyInput = (value: string): string => {
  // Remove tudo que não é número
  const numbers = value.replace(/\D/g, "");
  if (!numbers) return "";

  // Converte para número e divide por 100 para ter os centavos
  const amount = parseInt(numbers) / 100;

  // Formata com separador de milhar e decimal brasileiro
  return amount.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

// Converte valor formatado para número
const parseCurrencyToNumber = (value: string): number => {
  if (!value) return 0;
  // Remove pontos (milhar) e troca vírgula por ponto
  const cleaned = value.replace(/\./g, "").replace(",", ".");
  return parseFloat(cleaned) || 0;
};

// Formata número para exibição no input
const numberToCurrencyDisplay = (value: number | string): string => {
  const num = typeof value === "string" ? parseFloat(value) || 0 : value;
  if (num === 0) return "0,00";
  return num.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

// Formata data para o input date (YYYY-MM-DD) sem problema de timezone
const formatDateForInput = (dateStr: string): string => {
  if (!dateStr) return "";
  // Se já está no formato YYYY-MM-DD, retorna direto
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    return dateStr;
  }
  // Se vier com timezone (2026-05-01T00:00:00.000000Z), extrai só a data
  const match = dateStr.match(/^(\d{4}-\d{2}-\d{2})/);
  if (match) {
    return match[1];
  }
  return dateStr;
};

interface CommissionRule {
  id: number;
  uuid: string;
  name: string;
  start_date: string;
  end_date: string;
  is_active: boolean;
  apply_to_all_unities: boolean;
  tiers: Array<{
    id: number;
    uuid: string;
    tier_number: number;
    min_value: string;
    max_value: string | null;
    rate_below_goal: string;
    rate_at_goal: string;
    rate_above_goal: string;
  }>;
  unities: Array<{ id: number; name: string }>;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  rule: CommissionRule | null;
}

interface TierFormData {
  tier_number: number;
  min_value: string;
  max_value: string;
  rate_below_goal: string;
  rate_at_goal: string;
  rate_above_goal: string;
}

interface Unity {
  id: number;
  uuid: string;
  name: string;
}

export const CommissionRuleFormModal = ({ isOpen, onClose, onSuccess, rule }: Props) => {
  const [loading, setLoading] = useState(false);
  const [unities, setUnities] = useState<Unity[]>([]);

  const [name, setName] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [applyToAllUnities, setApplyToAllUnities] = useState(true);
  const [selectedUnityIds, setSelectedUnityIds] = useState<string[]>([]);
  const [tiers, setTiers] = useState<TierFormData[]>([
    {
      tier_number: 1,
      min_value: "0",
      max_value: "",
      rate_below_goal: "0",
      rate_at_goal: "0",
      rate_above_goal: "0",
    },
  ]);

  useEffect(() => {
    loadUnities();
  }, []);

  useEffect(() => {
    if (rule) {
      setName(rule.name);
      setStartDate(formatDateForInput(rule.start_date));
      setEndDate(formatDateForInput(rule.end_date));
      setIsActive(rule.is_active);
      setApplyToAllUnities(rule.apply_to_all_unities);
      setSelectedUnityIds(rule.unities?.map((u) => u.id.toString()) || []);
      setTiers(
        rule.tiers?.map((t) => ({
          tier_number: t.tier_number,
          min_value: numberToCurrencyDisplay(t.min_value),
          max_value: t.max_value ? numberToCurrencyDisplay(t.max_value) : "",
          rate_below_goal: t.rate_below_goal,
          rate_at_goal: t.rate_at_goal,
          rate_above_goal: t.rate_above_goal,
        })) || []
      );
    } else {
      resetForm();
    }
  }, [rule, isOpen]);

  const loadUnities = async () => {
    try {
      const { data } = await getUnitiesData(1);
      setUnities(data?.response?.data || []);
    } catch (error) {
      console.error("Erro ao carregar unidades:", error);
    }
  };

  const resetForm = () => {
    setName("");
    setStartDate("");
    setEndDate("");
    setIsActive(true);
    setApplyToAllUnities(true);
    setSelectedUnityIds([]);
    setTiers([
      {
        tier_number: 1,
        min_value: "0",
        max_value: "",
        rate_below_goal: "0",
        rate_at_goal: "0",
        rate_above_goal: "0",
      },
    ]);
  };

  const addTier = () => {
    const lastTier = tiers[tiers.length - 1];
    const lastMaxValue = lastTier?.max_value ? parseCurrencyToNumber(lastTier.max_value) : 0;
    const newMinValue = lastMaxValue > 0
      ? numberToCurrencyDisplay(lastMaxValue + 0.01)
      : "0,00";

    setTiers([
      ...tiers,
      {
        tier_number: tiers.length + 1,
        min_value: newMinValue,
        max_value: "",
        rate_below_goal: "0",
        rate_at_goal: "0",
        rate_above_goal: "0",
      },
    ]);
  };

  const removeTier = (index: number) => {
    if (tiers.length <= 1) {
      toast.error("É necessário ter pelo menos uma faixa");
      return;
    }
    const newTiers = tiers.filter((_, i) => i !== index);
    setTiers(
      newTiers.map((t, i) => ({
        ...t,
        tier_number: i + 1,
      }))
    );
  };

  const updateTier = (index: number, field: keyof TierFormData, value: string) => {
    const newTiers = [...tiers];

    // Para campos de valor, formata como moeda
    if (field === "min_value" || field === "max_value") {
      newTiers[index] = { ...newTiers[index], [field]: formatCurrencyInput(value) };

      // Atualiza o min_value da próxima faixa se alterou max_value
      if (field === "max_value" && index < tiers.length - 1 && value) {
        const maxNum = parseCurrencyToNumber(formatCurrencyInput(value));
        newTiers[index + 1] = { ...newTiers[index + 1], min_value: numberToCurrencyDisplay(maxNum + 0.01) };
      }
    } else {
      newTiers[index] = { ...newTiers[index], [field]: value };
    }

    setTiers(newTiers);
  };

  const handleSubmit = async () => {
    if (!name || !startDate || !endDate) {
      toast.error("Preencha todos os campos obrigatórios");
      return;
    }

    if (tiers.length === 0) {
      toast.error("Adicione pelo menos uma faixa de premiação");
      return;
    }

    setLoading(true);

    try {
      const payload = {
        name,
        start_date: startDate,
        end_date: endDate,
        is_active: isActive,
        apply_to_all_unities: applyToAllUnities,
        unity_ids: applyToAllUnities ? [] : selectedUnityIds.map(Number),
        tiers: tiers.map((t) => ({
          tier_number: t.tier_number,
          min_value: parseCurrencyToNumber(t.min_value),
          max_value: t.max_value ? parseCurrencyToNumber(t.max_value) : null,
          rate_below_goal: parseFloat(t.rate_below_goal) || 0,
          rate_at_goal: parseFloat(t.rate_at_goal) || 0,
          rate_above_goal: parseFloat(t.rate_above_goal) || 0,
        })),
      };

      if (rule) {
        await updateSellerCommissionRule(rule.uuid, payload);
        toast.success("Regra atualizada com sucesso");
      } else {
        await createSellerCommissionRule(payload);
        toast.success("Regra criada com sucesso");
      }

      onSuccess();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Erro ao salvar regra");
    }

    setLoading(false);
  };

  const unityOptions = unities.map((u) => ({
    value: u.id.toString(),
    label: u.name,
  }));

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Percent className="w-5 h-5" />
            {rule ? "Editar Regra de Premiação" : "Nova Regra de Premiação"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Informações da Regra */}
          <div className="space-y-4">
            <h3 className="font-semibold text-sm text-muted-foreground">
              Informações da Regra
            </h3>
            <div className="grid grid-cols-4 gap-4">
              <div className="col-span-2">
                <Label htmlFor="name">Nome da Regra</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Ex: Premiação Vendas - 2026"
                />
              </div>
              <div>
                <Label htmlFor="start_date">Data Início</Label>
                <Input
                  id="start_date"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="end_date">Data Fim</Label>
                <Input
                  id="end_date"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center gap-3">
                <Switch
                  checked={isActive}
                  onCheckedChange={setIsActive}
                  id="is_active"
                />
                <Label htmlFor="is_active">Regra Ativa</Label>
              </div>
              <div className="flex items-center gap-3">
                <Switch
                  checked={applyToAllUnities}
                  onCheckedChange={setApplyToAllUnities}
                  id="apply_all"
                />
                <Label htmlFor="apply_all">Aplicar para Todas as Unidades</Label>
              </div>
            </div>

            {!applyToAllUnities && (
              <div>
                <Label>Selecione as Unidades</Label>
                <MultiSelectCheckbox
                  options={unityOptions}
                  selectedValues={selectedUnityIds}
                  onSelectionChange={setSelectedUnityIds}
                  placeholder="Selecione as unidades..."
                  searchable
                />
              </div>
            )}
          </div>

          {/* Faixas de Premiação */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-sm text-muted-foreground">
                Faixas de Premiação
              </h3>
              <Button variant="outline" size="sm" onClick={addTier}>
                <Plus className="w-4 h-4 mr-2" />
                Adicionar Faixa
              </Button>
            </div>

            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-16">Faixa</TableHead>
                    <TableHead>Valor Inicial</TableHead>
                    <TableHead>Valor Final</TableHead>
                    <TableHead>% Não Bateu Meta</TableHead>
                    <TableHead>% Bateu Meta</TableHead>
                    <TableHead>% Super Meta</TableHead>
                    <TableHead className="w-16">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tiers.map((tier, index) => (
                    <TableRow key={index}>
                      <TableCell className="font-medium">{tier.tier_number}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <span className="text-muted-foreground">R$</span>
                          <Input
                            type="text"
                            value={tier.min_value}
                            onChange={(e) => updateTier(index, "min_value", e.target.value)}
                            className="w-32 text-right"
                            placeholder="0,00"
                          />
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <span className="text-muted-foreground">R$</span>
                          <Input
                            type="text"
                            value={tier.max_value}
                            onChange={(e) => updateTier(index, "max_value", e.target.value)}
                            placeholder="Sem limite"
                            className="w-32 text-right"
                          />
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Input
                            type="number"
                            step="0.01"
                            value={tier.rate_below_goal}
                            onChange={(e) => updateTier(index, "rate_below_goal", e.target.value)}
                            className="w-20"
                          />
                          <span className="text-muted-foreground">%</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Input
                            type="number"
                            step="0.01"
                            value={tier.rate_at_goal}
                            onChange={(e) => updateTier(index, "rate_at_goal", e.target.value)}
                            className="w-20"
                          />
                          <span className="text-muted-foreground">%</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Input
                            type="number"
                            step="0.01"
                            value={tier.rate_above_goal}
                            onChange={(e) => updateTier(index, "rate_above_goal", e.target.value)}
                            className="w-20"
                          />
                          <span className="text-muted-foreground">%</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeTier(index)}
                          disabled={tiers.length <= 1}
                        >
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>

          {/* Botões */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={onClose} disabled={loading}>
              Cancelar
            </Button>
            <Button onClick={handleSubmit} disabled={loading}>
              <Save className="w-4 h-4 mr-2" />
              {loading ? "Salvando..." : "Salvar Regra"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
